import { DropdownValue } from 'ext-ui/components/Dropdown/hooks/DropdownState.reducer'
import React, { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoryDropdownV2 from 'src/components/ExpenseCategoryDropdownV2/ExpenseCategoryDropdownV2'
import ExpenseDetailStaticView from 'src/components/ExpenseDetailStaticView/ExpenseDetailStaticView'
import { useTranslate } from 'src/localization/Trns'
import {
  ConnectedPlatformStatus,
  ExpenseDetailsModel,
} from 'src/models/transaction.models'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import {
  ExpenseCategoryResponse,
  ExpenseCategoryType,
} from 'src/queries/organizations/expenseCategories.queries'
import { usePostSingleTransactionExpenseCategory } from 'src/queries/transactions.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { isTransactionFreeTextSaveOnBlurEnabled } from 'src/utils/featureFlags'

import * as styles from './TransactionExpenseDetailsFormV2.style'

interface TransactionExpenseDataFormProps {
  className?: string
  transactionId?: string
  transactionIsCleared: boolean
  expCatSchema: ExpenseCategoryResponse[]
  expenseDetails?: ExpenseDetailsModel[]
  onExpenseCategorySelectSuccess?: (
    categoryId: string,
    selectedValue: DropdownValue | undefined,
    isSuggested?: boolean,
  ) => void
}

const TransactionExpenseDetailsFormV2: React.FC<
  TransactionExpenseDataFormProps
> = (props): React.ReactElement => {
  const translate = useTranslate()
  const featureFlags = useSelector(selectFeatureFlaggerFromState)
  const saveFreeTextOnBlur =
    isTransactionFreeTextSaveOnBlurEnabled(featureFlags)

  const { mutateAsync: updateExpenseCategory } =
    usePostSingleTransactionExpenseCategory()

  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(props.transactionId)

  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Check if transaction is synced with integration (readonly mode)
  const syncedWithIntegration = useMemo(
    () =>
      activeTransactionIntegration !== undefined &&
      [
        ConnectedPlatformStatus.Pending,
        ConnectedPlatformStatus.Success,
      ].includes(activeTransactionIntegration.syncStatus),
    [activeTransactionIntegration],
  )

  // Map current expense details by category ID for quick lookup
  // Handle vendor/customer categories which match by type instead of ID
  const expenseDetailsByCategory = useMemo(() => {
    if (!props.expenseDetails) return {}

    return props.expenseDetails.reduce(
      (acc, detail) => {
        // Skip archived categories for form rendering, but keep for archived section
        if (detail.categoryArchivedAt) {
          return acc
        }
        // For vendor/customer categories, match by type since categoryId is empty
        if (
          detail.categoryType === ExpenseCategoryType.Vendor ||
          detail.categoryType === ExpenseCategoryType.Customer
        ) {
          const schemaCategory = props.expCatSchema.find(
            (cat) => cat.type === detail.categoryType,
          )
          if (schemaCategory) {
            acc[schemaCategory.id] = detail
          }
        } else {
          // For regular categories, match by categoryId
          acc[detail.categoryId] = detail
        }
        return acc
      },
      {} as Record<string, ExpenseDetailsModel>,
    )
  }, [props.expenseDetails, props.expCatSchema])

  // Get archived categories (not archived labels) to show separately
  const archivedExpenseDetails = useMemo(() => {
    if (!props.expenseDetails) return []
    return props.expenseDetails.filter((detail) => detail.categoryArchivedAt)
  }, [props.expenseDetails])

  const handleCategoryChange = async (
    categoryId: string,
    labelId: string,
  ): Promise<void> => {
    if (!props.transactionId) {
      return Promise.reject({
        error: translate(
          'Sorry, something went wrong. Please try again later.',
        ),
      })
    }

    await updateExpenseCategory({
      transactionId: props.transactionId,
      categoryId,
      body: {
        labelId: labelId || undefined,
      },
    })
  }

  // If synced with integration, render static view of existing expense details
  if (syncedWithIntegration) {
    const hasExpenseDetails =
      props.expenseDetails && props.expenseDetails.length > 0

    if (!hasExpenseDetails) {
      return (
        <div css={styles.noExpenseCategoriesMessage}>
          {translate(
            'No expense categories have been assigned to this transaction.',
          )}
        </div>
      )
    }

    return (
      <div className={props.className} css={styles.formContainer}>
        {props.expenseDetails?.map((ec, i) => (
          <ExpenseDetailStaticView key={i} expenseDetail={ec} />
        ))}
      </div>
    )
  }

  // Render editable form
  return (
    <div className={props.className}>
      <div css={styles.formContainer}>
        {props.expCatSchema.map((category, i) => {
          const currentDetail = expenseDetailsByCategory[category.id]
          const selectedValue = currentDetail && {
            value: currentDetail.labelId || currentDetail.labelName,
            label: currentDetail.labelName,
          }

          return (
            <div
              key={category.id}
              ref={(el) => {
                containerRefs.current[category.id] = el
              }}
              data-testid={'expCat'}
              data-haslabels={category.hasLabels}
              data-freetextallowed={category.freeTextAllowed}
              data-name={category.name}
            >
              <ExpenseCategoryDropdownV2
                data-testid={'expCat-dropdown'}
                context={ExpenseCategoriesContext.TRANSACTION}
                transactionIsCleared={props.transactionIsCleared}
                category={category}
                transactionId={props.transactionId}
                saveOnBlur={saveFreeTextOnBlur}
                selectedValue={selectedValue}
                dropdownContainer={
                  containerRefs.current[category.id] ?? undefined
                }
                onSelect={({ value }) =>
                  handleCategoryChange(category.id, value)
                }
                onSelectSuccess={(selectedValue, isSuggested) =>
                  props.onExpenseCategorySelectSuccess?.(
                    category.id,
                    selectedValue,
                    isSuggested,
                  )
                }
                label={category.name}
                expenseDetail={
                  currentDetail && {
                    ruleId: currentDetail.ruleId,
                    generatedByPrediction: currentDetail.generatedByPrediction,
                    errorMessage: currentDetail.errorMessage,
                  }
                }
              />
            </div>
          )
        })}
      </div>

      {/* Show archived categories below the form */}
      {archivedExpenseDetails.length > 0 && (
        <div css={styles.archivedDetailsContainer}>
          {archivedExpenseDetails.map((ec, i) => (
            <ExpenseDetailStaticView
              key={i}
              expenseDetail={{
                ...ec,
                categoryName: translate('${label} (Archived)', {
                  label: ec.categoryName,
                }),
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default TransactionExpenseDetailsFormV2
